// components/organisms/MobileMenu/MobileMenu.js

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FaTimes, FaChevronRight, FaArrowLeft } from 'react-icons/fa';

const MainLink = ({ page, className, onClick }) =>
    page.isNavGroup
        ? <span className={`${className} mobile-nav-link--group-label`}>{page.title}</span>
        : <a href={page.url} className={className} onClick={onClick}>{page.title}</a>;

const SubLink = ({ page, className, onClick }) =>
    page.isNavGroup
        ? <span className={`${className} mobile-nav-link--group-label`}>{page.title}</span>
        : <a href={page.url} className={className} onClick={onClick}>{page.title}</a>;

const MobileMenu = ({ primaryPages = [], isOpen = false, onClose }) => {
    const [activeSubmenu, setActiveSubmenu] = useState(null);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                if (activeSubmenu) {
                    setActiveSubmenu(null);
                } else {
                    onClose();
                }
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, activeSubmenu, onClose]);

    useEffect(() => {
        if (!isOpen) setActiveSubmenu(null);
    }, [isOpen]);

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    const handleSubmenuOpen = (pageId, e) => {
        e.preventDefault();
        e.stopPropagation();
        setActiveSubmenu(pageId);
    };

    const currentSubmenuPage = activeSubmenu
        ? primaryPages.find((page) => page.id === activeSubmenu)
        : null;

    return (
        <div
            className={`mobile-menu-overlay ${isOpen ? 'is-open' : ''}`}
            onClick={handleOverlayClick}
        >
            <div className={`mobile-menu ${isOpen ? 'is-open' : ''}`}>

                {/* Header */}
                <div className="mobile-menu-header">
                    {activeSubmenu ? (
                        <>
                            <button
                                className="mobile-menu-button"
                                onClick={() => setActiveSubmenu(null)}
                                aria-label="Back to main menu"
                            >
                                <FaArrowLeft className="icon-sm" />
                                <span>Back</span>
                            </button>
                            <button
                                className="mobile-menu-button"
                                onClick={onClose}
                                aria-label="Close menu"
                            >
                                <FaTimes className="icon-md" />
                            </button>
                        </>
                    ) : (
                        <>
                            <span className="mobile-menu-title">Navigation</span>
                            <button
                                className="mobile-menu-button"
                                onClick={onClose}
                                aria-label="Close menu"
                            >
                                <FaTimes className="icon-md" />
                            </button>
                        </>
                    )}
                </div>

                {/* Content */}
                <div className="mobile-menu-content">
                    {activeSubmenu ? (

                        /* Submenu view */
                        <nav>
                            <div className="mobile-submenu-header">
                                <div className="text-block-title">
                                    {currentSubmenuPage?.title}
                                </div>
                            </div>
                            <ul className="mobile-nav-list">
                                <li>
                                    <MainLink
                                        page={currentSubmenuPage}
                                        className="mobile-nav-link mobile-nav-main-link"
                                        onClick={onClose}
                                    />
                                </li>
                                <li className="nav-divider"></li>
                                {currentSubmenuPage?.secondaryPages?.map((subPage) => (
                                    <li key={subPage.id}>
                                        <SubLink
                                            page={subPage}
                                            className="mobile-nav-link"
                                            onClick={onClose}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </nav>

                    ) : (

                        /* Main menu view */
                        <nav>
                            <ul className="mobile-nav-list">
                                {primaryPages.map((page) => (
                                    <li key={page.id} className="mobile-nav-item">
                                        <div className="mobile-nav-row">
                                            <MainLink
                                                page={page}
                                                className="mobile-nav-link"
                                                onClick={onClose}
                                            />
                                            {page.secondaryPages?.length > 0 && (
                                                <button
                                                    className="mobile-nav-arrow"
                                                    onClick={(e) => handleSubmenuOpen(page.id, e)}
                                                    aria-label={`View ${page.title} submenu`}
                                                >
                                                    <FaChevronRight className="icon-sm" />
                                                </button>
                                            )}
                                        </div>
                                        <div className="nav-divider"></div>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                    )}
                </div>

            </div>
        </div>
    );
};

MobileMenu.propTypes = {
    primaryPages: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        isNavGroup: PropTypes.bool,
        secondaryPages: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
            title: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
            isNavGroup: PropTypes.bool,
        })),
    })),
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default MobileMenu;